'use client';
import React from 'react';

interface AdminKPIGridProps {
  stats: {
    totalQRGenerated: number;
    todayQRGenerated: number;
    totalDownloads: number;
    activeUsers: number;
    todayChange: number;
    totalChange: number;
    downloadsChange: number;
    usersChange: number;
  };
}

interface KPICardProps {
  title: string;
  value: string;
  change: number;
  changeLabel: string;
  icon: React.ReactNode;
  accent: string;
  accentBg: string;
  accentBorder: string;
}

function KPICard({ title, value, change, changeLabel, icon, accent, accentBg, accentBorder }: KPICardProps) {
  const isPositive = change >= 0;
  return (
    <div className="bg-card rounded-3xl border border-border shadow-card p-5 card-hover">
      <div className="flex items-start justify-between mb-4">
        <div
          className="w-10 h-10 rounded-2xl flex items-center justify-center"
          style={{ background: accentBg, border: `1px solid ${accentBorder}` }}
        >
          <span style={{ color: accent }}>{icon}</span>
        </div>
        <span
          className={`badge ${isPositive ? 'badge-green' : 'badge-rose'}`}
        >
          {isPositive ? '+' : ''}{change}{changeLabel}
        </span>
      </div>
      <p className="font-extrabold text-foreground" style={{ fontSize: '30px', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{value}</p>
      <p className="text-muted-foreground mt-1" style={{ fontSize: '13px', fontWeight: 500 }}>{title}</p>
    </div>
  );
}

export default function AdminKPIGrid({ stats }: AdminKPIGridProps) {
  const cards: KPICardProps[] = [
    {
      title: 'Total QR Codes Generated',
      value: stats.totalQRGenerated.toLocaleString(),
      change: stats.totalChange,
      changeLabel: '% this week',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
          <rect x="11" y="1.5" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
          <rect x="1.5" y="11" width="5.5" height="5.5" rx="1.2" stroke="currentColor" strokeWidth="1.6" />
          <rect x="3.2" y="3.2" width="2" height="2" rx="0.4" fill="currentColor" />
          <rect x="12.8" y="3.2" width="2" height="2" rx="0.4" fill="currentColor" />
          <rect x="3.2" y="12.8" width="2" height="2" rx="0.4" fill="currentColor" />
          <rect x="11" y="11" width="2" height="2" rx="0.4" fill="currentColor" />
        </svg>
      ),
      accent: '#2563EB',
      accentBg: 'rgba(37,99,235,0.08)',
      accentBorder: 'rgba(37,99,235,0.16)',
    },
    {
      title: "Today's QR Codes",
      value: stats.todayQRGenerated.toString(),
      change: stats.todayChange,
      changeLabel: ' vs yesterday',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <circle cx="9" cy="9" r="7.5" />
          <polyline points="9,5 9,9 12,11" />
        </svg>
      ),
      accent: '#7C3AED',
      accentBg: 'rgba(124,58,237,0.08)',
      accentBorder: 'rgba(124,58,237,0.16)',
    },
    {
      title: 'Total Downloads',
      value: stats.totalDownloads.toLocaleString(),
      change: stats.downloadsChange,
      changeLabel: '% this week',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <path d="M9 11.5V3.5M9 11.5L6 8.5M9 11.5L12 8.5" />
          <path d="M2.5 14.5h13" />
        </svg>
      ),
      accent: '#059669',
      accentBg: 'rgba(5,150,105,0.08)',
      accentBorder: 'rgba(5,150,105,0.16)',
    },
    {
      title: 'Active Users',
      value: stats.activeUsers.toString(),
      change: stats.usersChange,
      changeLabel: '% this month',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
          <circle cx="9" cy="6" r="3.5" />
          <path d="M2.5 16c0-3.5 3-6 6.5-6s6.5 2.5 6.5 6" />
        </svg>
      ),
      accent: '#D97706',
      accentBg: 'rgba(217,119,6,0.08)',
      accentBorder: 'rgba(217,119,6,0.16)',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {cards.map((card, i) => (
        <KPICard key={`kpi-${i + 1}`} {...card} />
      ))}
    </div>
  );
}